/**
 * ============================================================================
 *  File        : frontend/src/components/FreeeSlaPanel.tsx
 *  Module      : components
 *  Purpose     : Freee SLA（計時 + 提醒事件，不串接 Freee API）+ 財務書類參照 panel
 *                （v1.3-11；spec §10.3 + §B4 + CLAUDE.md §0）。嵌在案件子頁
 *                （CaseFinalReportPage / S9）內，不新增 caseRoutes 路徑。
 *  Author      : EF Engineering
 *  Created     : 2026-06-02
 *  Updated     : 2026-06-02
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  **Freee API は呼ばない**（CLAUDE.md §0）。本 panel：
 *    1. Freee SLA タイマー一覧（pending / warning / overdue / completed）+ 完了マーク。
 *    2. 財務書類参照（freee 管理番号 / 請求書番号 / 仕切書番号）の一覧 + 記録フォーム。
 *
 *  RBAC：list = view perms；complete / record = action perms（後端 v1.rs gate 為準，
 *  前端只控制入口顯示）。
 */

import { type ReactElement } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  completeFreeeTask,
  listFinancialDocuments,
  listFreeeTasks,
  recordFinancialDocument,
  type FinancialDocumentDto,
  type FreeeTaskDto,
} from '../api/freee'
import { ApiError } from '../api/client'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Input } from './ui/input'
import { Separator } from './ui/separator'
import { Skeleton } from './ui/skeleton'
import { toast } from './ui/sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table'

interface PanelProps {
  t: (key: string) => string
  caseId: string
}

function errDetail(error: unknown): string {
  return error instanceof ApiError ? `（${error.message}）` : ''
}

/** SLA 状態 → Badge variant（overdue は破壊色、completed は outline）。 */
function statusVariant(status: string): 'secondary' | 'destructive' | 'outline' {
  if (status === 'overdue') return 'destructive'
  if (status === 'completed') return 'outline'
  return 'secondary'
}

function shortDate(iso: string): string {
  // RFC3339 → 日付部分のみ（タイムゾーンノイズを避ける）。
  return iso.slice(0, 10)
}

export function FreeeSlaPanel({ t, caseId }: PanelProps): ReactElement {
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canViewSla = can(PERMISSIONS.freeeSlaView)
  const canComplete = can(PERMISSIONS.freeeSlaComplete)
  const canViewDoc = can(PERMISSIONS.financialDocumentView)
  const canCreateDoc = can(PERMISSIONS.financialDocumentCreate)

  const tasksQuery = useQuery({
    queryKey: ['case', caseId, 'freee-tasks'],
    queryFn: () => listFreeeTasks(caseId),
    enabled: caseId !== '' && canViewSla,
  })
  const docsQuery = useQuery({
    queryKey: ['case', caseId, 'financial-documents'],
    queryFn: () => listFinancialDocuments(caseId),
    enabled: caseId !== '' && canViewDoc,
  })

  const completeMutation = useMutation({
    mutationFn: (taskId: number) => completeFreeeTask(caseId, taskId),
    onSuccess: () => {
      toast.success(t('freee.sla.complete.success'))
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'freee-tasks'] })
    },
    onError: (error) => toast.error(`${t('freee.sla.complete.error')}${errDetail(error)}`),
  })

  const tasks: FreeeTaskDto[] = tasksQuery.data?.data ?? []
  const docs: FinancialDocumentDto[] = docsQuery.data?.data ?? []

  const typeLabel = (docType: string): string =>
    docType === 'invoice' ? t('freee.sla.type.invoice') : t('freee.sla.type.receipt')
  const statusLabel = (status: string): string => {
    if (status === 'pending') return t('freee.sla.status.pending')
    if (status === 'warning') return t('freee.sla.status.warning')
    if (status === 'overdue') return t('freee.sla.status.overdue')
    if (status === 'completed') return t('freee.sla.status.completed')
    return status
  }

  return (
    <Card data-testid="freee-sla-panel">
      <CardHeader>
        <CardTitle>{t('freee.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">{t('freee.subtitle')}</p>

        {/* ---- Freee SLA タイマー一覧 ---- */}
        <section className="space-y-2" data-testid="freee-sla-section">
          <h3 className="text-sm font-medium">{t('freee.sla.heading')}</h3>
          {!canViewSla ? (
            <Badge variant="outline">{t('freee.rbac.forbidden')}</Badge>
          ) : tasksQuery.isPending ? (
            <Skeleton className="h-20 w-full" />
          ) : tasksQuery.isError ? (
            <p className="text-sm text-destructive">{t('freee.sla.loadError')}</p>
          ) : tasks.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('freee.sla.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('freee.sla.col.phase')}</TableHead>
                    <TableHead>{t('freee.sla.col.type')}</TableHead>
                    <TableHead>{t('freee.sla.col.status')}</TableHead>
                    <TableHead>{t('freee.sla.col.issuedAt')}</TableHead>
                    <TableHead>{t('freee.sla.col.dueAt')}</TableHead>
                    <TableHead className="text-right">{t('freee.sla.col.action')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasks.map((task) => (
                    <TableRow key={task.id} data-testid={`freee-task-row-${task.id}`}>
                      <TableCell className="uppercase">{task.settlement_phase}</TableCell>
                      <TableCell>{typeLabel(task.task_type)}</TableCell>
                      <TableCell>
                        <Badge
                          variant={statusVariant(task.status)}
                          data-testid={`freee-task-status-${task.id}`}
                        >
                          {statusLabel(task.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>{shortDate(task.issued_at)}</TableCell>
                      <TableCell>{shortDate(task.due_at)}</TableCell>
                      <TableCell className="text-right">
                        {canComplete && task.status !== 'completed' ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            data-testid={`freee-task-complete-${task.id}`}
                            disabled={completeMutation.isPending}
                            onClick={() => completeMutation.mutate(task.id)}
                          >
                            {t('freee.sla.complete.button')}
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

        <Separator />

        {/* ---- 財務書類参照（記録フォーム + 一覧）---- */}
        <section className="space-y-2" data-testid="freee-doc-section">
          <h3 className="text-sm font-medium">{t('freee.doc.heading')}</h3>
          {!canViewDoc ? (
            <Badge variant="outline">{t('freee.rbac.forbidden')}</Badge>
          ) : (
            <>
              {canCreateDoc ? (
                <FinancialDocumentForm t={t} caseId={caseId} />
              ) : null}
              {docsQuery.isPending ? (
                <Skeleton className="h-16 w-full" />
              ) : docsQuery.isError ? (
                <p className="text-sm text-destructive">{t('freee.doc.loadError')}</p>
              ) : docs.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('freee.doc.empty')}</p>
              ) : (
                <div className="overflow-x-auto rounded-md border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('freee.doc.col.type')}</TableHead>
                        <TableHead>{t('freee.doc.col.freeeNo')}</TableHead>
                        <TableHead>{t('freee.doc.col.invoiceNo')}</TableHead>
                        <TableHead>{t('freee.doc.col.statementNo')}</TableHead>
                        <TableHead>{t('freee.doc.col.note')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {docs.map((doc) => (
                        <TableRow key={doc.id} data-testid={`freee-doc-row-${doc.id}`}>
                          <TableCell>{typeLabel(doc.doc_type)}</TableCell>
                          <TableCell>{doc.freee_management_no ?? '—'}</TableCell>
                          <TableCell>{doc.invoice_no ?? '—'}</TableCell>
                          <TableCell>{doc.settlement_statement_no ?? '—'}</TableCell>
                          <TableCell className="max-w-[16rem] whitespace-pre-wrap text-sm">
                            {doc.note ?? '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </section>
      </CardContent>
    </Card>
  )
}

// ---- 財務書類参照 記録フォーム --------------------------------------------

interface DocFormValues {
  docType: 'invoice' | 'receipt'
  freeeManagementNo: string
  invoiceNo: string
  settlementStatementNo: string
  attachmentRef: string
  note: string
}

function FinancialDocumentForm({ t, caseId }: PanelProps): ReactElement {
  const queryClient = useQueryClient()
  const { register, handleSubmit, reset } = useForm<DocFormValues>({
    defaultValues: {
      docType: 'invoice',
      freeeManagementNo: '',
      invoiceNo: '',
      settlementStatementNo: '',
      attachmentRef: '',
      note: '',
    },
  })

  const mutation = useMutation({
    mutationFn: (v: DocFormValues) =>
      recordFinancialDocument(caseId, {
        doc_type: v.docType,
        freee_management_no: v.freeeManagementNo.trim() || null,
        invoice_no: v.invoiceNo.trim() || null,
        settlement_statement_no: v.settlementStatementNo.trim() || null,
        attachment_ref: v.attachmentRef.trim() || null,
        note: v.note.trim() || null,
      }),
    onSuccess: () => {
      toast.success(t('freee.doc.form.success'))
      reset()
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'financial-documents'] })
    },
    onError: (error) => toast.error(`${t('freee.doc.form.error')}${errDetail(error)}`),
  })

  const submit = handleSubmit((v) => mutation.mutate(v))

  return (
    <form
      className="space-y-3 rounded-md border border-border p-3"
      data-testid="freee-doc-form"
      onSubmit={(e) => void submit(e)}
    >
      <p className="text-sm font-medium">{t('freee.doc.form.heading')}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">{t('freee.doc.form.docType')}</span>
          <select
            data-testid="freee-doc-type"
            aria-label={t('freee.doc.form.docType')}
            className="flex h-9 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            {...register('docType', { required: true })}
          >
            <option value="invoice">{t('freee.sla.type.invoice')}</option>
            <option value="receipt">{t('freee.sla.type.receipt')}</option>
          </select>
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">{t('freee.doc.form.freeeNo')}</span>
          <Input
            data-testid="freee-doc-freee-no"
            aria-label={t('freee.doc.form.freeeNo')}
            {...register('freeeManagementNo')}
          />
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">{t('freee.doc.form.invoiceNo')}</span>
          <Input
            data-testid="freee-doc-invoice-no"
            aria-label={t('freee.doc.form.invoiceNo')}
            {...register('invoiceNo')}
          />
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">{t('freee.doc.form.statementNo')}</span>
          <Input
            data-testid="freee-doc-statement-no"
            aria-label={t('freee.doc.form.statementNo')}
            {...register('settlementStatementNo')}
          />
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">{t('freee.doc.form.attachment')}</span>
          <Input
            data-testid="freee-doc-attachment"
            aria-label={t('freee.doc.form.attachment')}
            {...register('attachmentRef')}
          />
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">{t('freee.doc.form.note')}</span>
          <Input
            data-testid="freee-doc-note"
            aria-label={t('freee.doc.form.note')}
            {...register('note')}
          />
        </label>
      </div>
      <Button
        type="submit"
        size="sm"
        data-testid="freee-doc-submit"
        disabled={mutation.isPending}
      >
        {t('freee.doc.form.submit')}
      </Button>
    </form>
  )
}
